import { BlockScroll } from "@birdwatch/ui";
import { commitFieldValue, Form } from "@birdwatch/ui/form";
import type { Extensions } from "@tiptap/core";
import { cn } from "cn";
import { type ReactNode, startTransition, useMemo } from "react";
import { PreviewUploadAttachments } from "@/components/ui/attachment/preview-upload-attachments";
import {
  BubbleMenu,
  Editor,
  EmojiPicker,
  InlineMenu,
  LinkDialog,
  MentionButton,
  ToggleInlineMenuButton,
} from "@/components/ui/editor/rich-text";
import { SendButton } from "@/components/ui/message";
import {
  AudioRecorderPopover,
  AudioRecorderPreview,
} from "@/features/shared/audio-recorder";
import { Upload } from "@/features/shared/upload";
import { RichTextContentField } from "@/fields";
import { useFileDrop } from "@/hooks/use-file-drop";
import { getAttachmentsDropZoneProps } from "@/libs/attachment-drag";
import type { ChatComposerApi } from "./_types/chat-composer";

export type ChatComposerSendContext = {
  submit: () => void;
  requestSend: () => void;
  isSubmitting: boolean;
  isUploading: boolean;
  isDisabled: boolean;
};

export type ChatComposerProps = {
  composer: ChatComposerApi;
  placeholder?: string;
  /** Caller-supplied Tiptap extensions (mentions). Mod-Enter is added by the composer. */
  extensions?: Extensions;
  showMentionButton?: boolean;
  showAudioRecorder?: boolean;
  /** OS file drag-and-drop onto the box. Default true. */
  enableFileDrop?: boolean;
  /** Rendered first in the action bar, after the invisible uploader. */
  leadingActions?: ReactNode;
  /** Rendered last in the action bar, before the send button. */
  trailingActions?: ReactNode;
  sendLabel?: string;
  sendTooltip?: string;
  /** Full override of the send control (icon buttons, cancel pairs). Wins over sendLabel/sendTooltip. */
  sendSlot?: (ctx: ChatComposerSendContext) => ReactNode;
  /** Above the box, inside <Form>. */
  header?: ReactNode;
  /** Inside the box, above the text — what the message answers (a reply-to card). */
  innerHeader?: ReactNode;
  /** Below the box, inside <Form>. */
  footer?: ReactNode;
  /** Applied to the bordered box. */
  className?: string;
  disabled?: boolean;
};

export function ChatComposer({
  composer,
  placeholder,
  extensions,
  showMentionButton = false,
  showAudioRecorder = false,
  enableFileDrop = true,
  leadingActions,
  trailingActions,
  sendLabel = "Send",
  sendTooltip = "Send message",
  sendSlot,
  header,
  innerHeader,
  footer,
  className,
  disabled,
}: ChatComposerProps) {
  const {
    form,
    editorRef,
    uploadRef,
    audioRecorderRef,
    uploadTriggerRef,
    attachments,
    audios,
    isSubmitting,
    isUploading,
    isDisabled,
    limitError,
    submit,
    requestSend,
    keyboardExtensions,
    draft,
    uploadScope,
    typingEnabled,
    stopTyping,
  } = composer;

  const { dropRef, isDragging } = useFileDrop({
    onUploadFiles: (files: File[]) => {
      if (!enableFileDrop) return;
      uploadRef.current?.uploadFiles(files);
    },
  });

  // Built once — Tiptap freezes its extension list at editor construction.
  const allExtensions = useMemo(
    () => [...(extensions ?? []), ...keyboardExtensions],
    [extensions, keyboardExtensions],
  );

  const dropZoneProps = enableFileDrop
    ? getAttachmentsDropZoneProps((dropped) =>
        uploadRef.current?.addAttachments(dropped),
      )
    : {};

  const removeAudio = (audioId: string) => {
    const next = (audios ?? []).filter((a: any) => a.id !== audioId);
    form.setFieldValue("audios", next);
    startTransition(() => draft?.setAudios?.(next));
  };

  // A lone recording fills the row; past one they sit back so the next one peeks.
  const audioWidth = audios.length === 1 ? "w-full" : "w-[90%]";

  const hasAttachments = attachments.length > 0 || audios.length > 0;

  return (
    <Form form={form}>
      {header}
      <Editor>
        <div
          ref={enableFileDrop ? dropRef : undefined}
          {...dropZoneProps}
          className={cn(
            /* sorted */
            "overflow-hidden",
            "w-full",
            "border border-border-dark-low rounded-2xl",
            "transition-all duration-200",
            "focus-within:border-border-dark-high focus-within:shadow-md",
            {
              "border-dashed border-border-dark-high bg-bg-surface-elevate-high":
                isDragging,
            },
            className,
          )}
        >
          {innerHeader}
          {audios.length > 0 && (
            <BlockScroll
              axis="x"
              className="flex items-center gap-2 shrink-0 p-2 scrollbar-hidden"
            >
              {audios.map((audioItem: any) => (
                <AudioRecorderPreview
                  key={audioItem.id}
                  audioAttachment={audioItem}
                  onRemove={() => removeAudio(audioItem.id)}
                  className={cn("shrink-0", audioWidth)}
                />
              ))}
            </BlockScroll>
          )}
          {attachments.length > 0 && (
            <BlockScroll axis="x" className="shrink-0 p-2 scrollbar-hidden">
              <PreviewUploadAttachments
                uploadRef={uploadRef}
                attachments={attachments}
                className="w-max"
              />
            </BlockScroll>
          )}
          <BubbleMenu />
          <InlineMenu
            className={cn(hasAttachments && "border-t border-border-dark-min")}
          />
          <LinkDialog />
          <RichTextContentField
            name="content"
            ref={editorRef}
            placeholder={placeholder}
            disabled={disabled}
            extensions={allExtensions}
            onUploadImages={(files) => uploadRef.current?.uploadFiles(files)}
            onValueChange={(value) =>
              startTransition(() => draft?.setContent(value))
            }
            onBlur={() => {
              if (typingEnabled) stopTyping();
            }}
          />

          {limitError && (
            <p
              role="alert"
              className={cn(
                /* sorted */
                "px-4 pb-1",
                "text-text-negative text-sm",
              )}
            >
              {limitError}
            </p>
          )}

          <div className="flex items-center justify-between h-12 px-2 shrink-0">
            <div className="flex items-center gap-1">
              <form.AppField name="attachments">
                {(field) => (
                  <Upload
                    ref={uploadRef}
                    scope={uploadScope}
                    value={field.state.value}
                    onChange={(value) => {
                      // Discrete commit — adding/removing a file is a commit.
                      commitFieldValue(field, value);
                      startTransition(() =>
                        draft?.setAttachments(value as any),
                      );
                    }}
                  >
                    <button type="button" ref={uploadTriggerRef} hidden />
                  </Upload>
                )}
              </form.AppField>

              {leadingActions}
              {showMentionButton && <MentionButton />}
              <EmojiPicker />
              <ToggleInlineMenuButton />
              {showAudioRecorder && (
                <form.AppField name="audios">
                  {(field) => (
                    <AudioRecorderPopover
                      ref={audioRecorderRef}
                      onChange={(value) => {
                        if (!value) return;
                        const next = [
                          ...((field.state.value as any[]) ?? []),
                          value,
                        ];
                        commitFieldValue(field, next);
                        startTransition(() =>
                          draft?.setAudios?.(
                            next.map((v: any) => ({
                              id: v.id,
                              status: v.status,
                              percent: v.percent,
                              originalContentUri: v.originalContentUri,
                            })) as any,
                          ),
                        );
                      }}
                    />
                  )}
                </form.AppField>
              )}
              {trailingActions}
            </div>

            {sendSlot ? (
              sendSlot({
                submit,
                requestSend,
                isSubmitting,
                isUploading,
                isDisabled,
              })
            ) : (
              <SendButton
                label={sendLabel}
                tooltipDescription={sendTooltip}
                onClick={requestSend}
                isLoading={isSubmitting}
                disabled={isDisabled}
              />
            )}
          </div>
        </div>
      </Editor>
      {footer}
    </Form>
  );
}
